import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { dataService } from '../services/dataService';

import { permitScope } from '../services/permitService';
// Helper Hook
export const useData = () => {
    return useContext(DataContext);
};

export const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();

    // Application Data State
    const [globalLibrary, setGlobalLibrary] = useState(null);
    const [taskMetadata, setTaskMetadata] = useState(null);
    const [templateConfig, setTemplateConfig] = useState(null);
    const [globalTools, setGlobalTools] = useState([]);
    const [globalPPEs, setGlobalPPEs] = useState([]);
    const [mechanicChecksLibrary, setMechanicChecksLibrary] = useState(null);
    const [configDisciplines, setConfigDisciplines] = useState([]);
    const [configWaste, setConfigWaste] = useState([]);
    const [componentMetadata, setComponentMetadata] = useState(null);
    const [systemSettings, setSystemSettings] = useState({});

    // Admin Data State
    const [adminData, setAdminData] = useState(null); // { users, locations }
    const [locationData, setLocationData] = useState(null); // Current selected location details

    // Status State
    const [dataLoading, setDataLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState('');
    const [dataError, setDataError] = useState(null);
    const [logs, setLogs] = useState([]);

    const dataLoadedRef = useRef(false);

    const addLog = (msg) => setLogs(prev => [...prev, msg]);

    useEffect(() => {
        const loadData = async () => {
            if (authLoading) return; // Wait for auth

            if (!user) {
                // Determine clean up if logged out
                setGlobalLibrary(null);
                setAdminData(null);
                dataLoadedRef.current = false;
                return;
            }

            // Onboarding: ingelogd maar nog zonder profiel. Niets laden en
            // vooral dataLoadedRef niet zetten, anders telt deze ronde als
            // "geladen" en slaat de echte lading na de registratie over. Dan
            // opent het beheerscherm zonder locaties.
            if (user.geenProfiel) {
                dataLoadedRef.current = false;
                return;
            }

            // If already loaded and user role hasn't changed requirement (simplified check for now)
            // Ideally we check if we have the data we need.
            // For now, simple idempotent check:
            if (dataLoadedRef.current) {
                // Check if we need to upgrade to admin data
                const isPrivileged = user.role === 'admin' || user.role === 'superuser' || user.role === 'wv';
                if (isPrivileged && !adminData) {
                    // Proceed to fetch admin data
                } else {
                    return;
                }
            }

            setDataLoading(true);
            setLoadingStatus('Systeemgegevens ophalen...');

            try {
                // 1. Fetch Global Library (Foundational Data)
                const libraryData = await dataService.getGlobalLibrary();
                setGlobalLibrary(libraryData.onderdeel_tasks);
                setTaskMetadata(libraryData.task_metadata);
                setComponentMetadata(libraryData.component_metadata);
                setTemplateConfig(libraryData.template_config);
                setGlobalTools(libraryData.global_tools || []);
                setGlobalPPEs(libraryData.global_ppes || []);
                setMechanicChecksLibrary(libraryData.mechanic_checks_library || null);
                setConfigDisciplines(libraryData.config_disciplines || []);
                setConfigWaste(libraryData.config_waste || []);
                setSystemSettings(libraryData.system_settings || {});

                // 2. Fetch Admin Data (if privileged)
                const isPrivileged = user.role === 'admin' || user.role === 'superuser' || user.role === 'wv';
                if (isPrivileged) {
                    setLoadingStatus('Locaties en gebruikers laden...');
                    addLog('Fetching Admin Data...');

                    const bereik = permitScope(user);
                    const allLocations = await dataService.getAllLocations(bereik);
                    const allUsers = await dataService.getAllUsers(bereik);

                    setAdminData({
                        locations: allLocations,
                        users: allUsers
                    });
                    addLog(`Loaded ${Object.keys(allLocations).length} locations.`);
                }

                dataLoadedRef.current = true;
                setDataError(null);

            } catch (err) {
                console.error("DataContext Error:", err);
                setDataError(err.message);
                addLog("Error loading data: " + err.message);
            } finally {
                setDataLoading(false);
            }
        };

        loadData();
    }, [user, authLoading]);

    // Actions
    const fetchLocationData = async (locationName) => {
        try {
            // Parallel fetch for speed
            const [locData, libData] = await Promise.all([
                dataService.getLocation(locationName),
                dataService.getLocationLibrary(locationName) // Also fetch local library!
            ]);

            // Merge library into location data (App.jsx expects 'onderdeel_tasks' or 'library')
            const fullData = {
                ...locData,
                // Ensure local tasks are available to App assembly logic
                onderdeel_tasks: libData?.onderdeel_tasks || {},
                task_metadata: libData?.task_metadata || {},
                component_metadata: libData?.component_metadata || {},
                library: libData // Backup key
            };


            setLocationData(fullData);
            return fullData;
        } catch (err) {
            console.error("Error fetching location:", err);
            throw err;
        }
    };

    const refreshAdminData = async () => {
        // Manual refresh logic
        if (!user) return;
        const bereik = permitScope(user);
        const allLocations = await dataService.getAllLocations(bereik);
        const allUsers = await dataService.getAllUsers(bereik);
        setAdminData({ locations: allLocations, users: allUsers });
    };

    // Update voice vocabulary (used by monteur learning loop + admin editor)
    const updateVocabulary = useCallback(async (newVocabulary) => {
        try {
            // Optimistic UI update
            setSystemSettings(prev => ({ ...prev, voice_vocabulary: newVocabulary }));
            // Persist to Firestore
            await dataService.saveSystemSettings({ voice_vocabulary: newVocabulary });
        } catch (error) {
            console.error('[DataContext] updateVocabulary failed:', error);
        }
    }, []);

    const value = {
        // State
        globalLibrary,
        taskMetadata,
        componentMetadata,
        templateConfig,
        globalTools,
        globalPPEs,
        mechanicChecksLibrary,
        configDisciplines,
        configWaste,
        adminData,
        locationData,
        dataLoading,
        loadingStatus,
        dataError,
        logs,
        systemSettings,
        // Actions
        addLog,
        fetchLocationData,
        refreshAdminData,
        updateVocabulary,
        setAdminData, // Allow manual updates (optimistic UI)
        setLocationData,
        // Expose Setters for Sync
        setGlobalLibrary,
        setTaskMetadata,
        setTemplateConfig,
        setGlobalTools,
        setGlobalPPEs,
        setMechanicChecksLibrary,
        setConfigDisciplines,
        setConfigWaste,
        setSystemSettings
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};
